import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ArrowRight, CheckCircle, Calendar, Users, Building, Target, TrendingUp, Shield, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { caseStudies } from "@/data/caseStudyData";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";
import { BreadcrumbNav } from "@/components/case-studies/BreadcrumbNav";

const OptimizedM365SecurityTransformation = () => {
  const caseStudy = caseStudies.find(study => study.id === "financial-services-m365");
  const companyName = caseStudy?.redactedName || "Financial Enterprise Group";
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="py-12 md:py-24 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <BreadcrumbNav currentPage="Financial Services M365 Security Optimization" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="mb-6">
                  <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    Case Study: Financial Services
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  How <RedactedCompanyName companyName={companyName} /> Optimizes Microsoft 365 Security & Saved $320K Annually
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Learn how we helped a financial services firm maximize their existing Microsoft 365 E5 investment, consolidate redundant tools, and significantly improve their security posture.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/get-started">
                    <Button 
                      size="lg"
                      className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 shadow-md group"
                    >
                      Get Similar Results
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <a href="#challenge">
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-purple-300 text-purple-800 hover:bg-purple-50"
                    >
                      Read the Case Study
                    </Button>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <Card className="bg-white border-0 shadow-xl rounded-xl overflow-hidden">
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Industry</p>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Financial Services</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company Size</p>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">500+ Employees</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Project Duration</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">4 Months</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Focus Areas</p>
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Microsoft 365 & Tool Consolidation</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-3">Key Results:</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">40% improvement in NIST CSF score</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">$320K annual security tool savings</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">85% reduction in security alerts</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge Section */}
        <section id="challenge" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-4">
                  The Challenge
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Tool Sprawl and Underutilized Licensing
                </h2>
                <p className="text-lg text-gray-600">
                  <RedactedCompanyName companyName={companyName} /> was facing significant challenges with their security infrastructure despite having already invested in premium Microsoft 365 E5 licenses.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Unused Premium Licensing</h3>
                  <p className="text-gray-600 mb-4">
                    Despite paying for Microsoft 365 E5 licenses with advanced security features, most capabilities remained unused or improperly configured.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Only using 15% of available security features in M365 E5</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Tool Sprawl</h3>
                  <p className="text-gray-600 mb-4">
                    The security team had accumulated 14 different security tools over time, many with overlapping capabilities already included in their Microsoft licensing.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Managing 14 different security tools with redundant functionality</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Alert Fatigue</h3>
                  <p className="text-gray-600 mb-4">
                    Security analysts were overwhelmed by thousands of daily alerts from multiple systems with no unified view or prioritization mechanism.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Over 3,000 daily security alerts with 60% false positive rate</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Poor NIST CSF Alignment</h3>
                  <p className="text-gray-600 mb-4">
                    Despite significant security spending, their NIST Cybersecurity Framework assessment showed poor alignment with industry best practices.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Only 42% alignment with NIST CSF despite high security spending</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 p-8 rounded-xl border border-red-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-red-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Breaking Point
                    </h3>
                    <p className="text-gray-700">
                      "We were spending over $500K annually on security tools, yet our NIST assessment came back with concerning gaps. At the same time, our security team was drowning in alerts while our Microsoft 365 E5 licenses—which we were already paying for—had advanced security features we weren't even using."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— CIO at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Solution Section */}
        <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  The Solution
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Maximizing Microsoft 365 Security & Streamlining Operations
                </h2>
                <p className="text-lg text-gray-600">
                  Our approach focused on a three-phase strategy to fully leverage their Microsoft 365 E5 investment while consolidating their security toolset.
                </p>
              </div>
              
              <div className="space-y-8 mb-12">
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Phase 1: Advisory Assessment & Roadmap</h3>
                      <p className="text-gray-600 mb-4">
                        Our advisory team conducted a comprehensive assessment of their existing security tools, Microsoft 365 configuration, and NIST CSF alignment.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Mapped all 14 existing security tools to Microsoft 365 E5 capabilities</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Developed tailored NIST CSF alignment roadmap with Microsoft technologies</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Created phased tool consolidation plan that minimized operational disruption</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Target className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Phase 2: Engineering Implementation</h3>
                      <p className="text-gray-600 mb-4">
                        Our engineering team implemented and optimized the Microsoft 365 security stack to replace and exceed the functionality of their existing tools.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Configured Microsoft Defender for Endpoint, Identity, Office 365, and Cloud Apps</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Built custom security dashboards in Microsoft Sentinel for unified visibility</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Implemented automated response playbooks for common security incidents</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Phase 3: Optimization & Knowledge Transfer</h3>
                      <p className="text-gray-600 mb-4">
                        We fine-tuned the security operations to reduce alert noise and provided comprehensive training for their internal team.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Tuned detection algorithms to reduce false positives by 85%</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Developed custom compliance reports mapped to NIST CSF controls</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Conducted hands-on training sessions for security team on new tools and processes</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 p-8 rounded-xl border border-purple-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-purple-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Transformation Moment
                    </h3>
                    <p className="text-gray-700">
                      "The moment it all clicked was when I saw our Microsoft Sentinel dashboard consolidating security alerts from across our organization—with clear prioritization and automated responses. We were finally getting the full value from our Microsoft investment while actually improving our security posture. Best of all, we were able to redirect significant budget from redundant tools to growth initiatives."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— CISO at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  The Results
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Measurable Security & Cost Improvements
                </h2>
                <p className="text-lg text-gray-600">
                  Within four months, <RedactedCompanyName companyName={companyName} /> achieved transformative improvements in both security posture and operational efficiency.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">40</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">NIST CSF Improvement</h3>
                  <p className="text-gray-600">
                    Increased NIST CSF alignment from 42% to 82% by properly implementing Microsoft 365 security capabilities and aligning them with framework controls.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">$320K</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Annual Cost Savings</h3>
                  <p className="text-gray-600">
                    Eliminated 9 redundant security tools, saving $320,000 annually in licensing and maintenance costs while improving overall security capabilities.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">85</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Alert Reduction</h3>
                  <p className="text-gray-600">
                    Reduced daily security alerts from over 3,000 to approximately 450 high-quality alerts with integrated context and automated response options.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">67</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Faster Incident Response</h3>
                  <p className="text-gray-600">
                    Reduced average security incident response time from 6 hours to under 2 hours through unified visibility and automated response workflows.
                  </p>
                </div>
              </div>
              
              <div className="bg-green-50 p-8 rounded-xl border border-green-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Long-Term Impact
                    </h3>
                    <p className="text-gray-700">
                      "What surprised us most was that by optimizing our Microsoft 365 security stack, we not only saved money but actually improved our security posture beyond what our previous patchwork of tools provided. The security budget we reclaimed has been redirected to strategic initiatives that support our growth, making security an enabler rather than just a cost center."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— CFO at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Ready to Maximize Your Microsoft 365 Investment?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Schedule a consultation to discover how we can help your organization achieve similar results with your existing Microsoft 365 licenses.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/get-started">
                  <Button 
                    size="xl"
                    className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 transition-colors px-8 py-6 h-auto shadow-lg hover:shadow-xl group"
                  >
                    Schedule Your Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/services/microsoft-365-security">
                  <Button
                    variant="outline"
                    size="xl"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-6 h-auto"
                  >
                    Explore Microsoft 365 Security Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OptimizedM365SecurityTransformation;
